import { useState } from 'react';

import { fmtDate } from '@dcc-erp/shared';
import type { SupplierBranch, VendorBankChange } from '@dcc-erp/shared';

import { useVendorBankChanges } from '../../lib/queries/suppliers';
import { Can } from '../Can';
import { VendorBankChangeApprovalPanel } from './VendorBankChangeApprovalPanel';
import { VendorBankChangeModal } from './VendorBankChangeModal';

interface Props {
  companyId: string | undefined;
  supplierId: string;
  vendorId: string;
  vendor: SupplierBranch;
}

// Mask an account number to its last 4 digits (the redirection target must never
// be shown in full on the read view; the full value lives only on the VBC request
// diff for the approver). '••••1234' for '111-1-11234-5'.
function maskAccount(acct: string | null | undefined): string {
  const digits = (acct ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  const last4 = digits.slice(-4);
  return `••••${last4}`;
}

// A short Thai label for the DECISION-Z status badge on each request row.
const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'ฉบับร่าง',
  PENDING: 'รออนุมัติ',
  APPLIED: 'เปลี่ยนแล้ว',
  REJECTED: 'ไม่อนุมัติ',
  RETURNED: 'ตีกลับแก้ไข',
};

// VendorBankSection — read-only masked CURRENT destination bank of one Vendor
// branch + the "ขอเปลี่ยนบัญชีธนาคาร" request entry point (D2: a bank change is a
// VBC 4-eyes request, NEVER a direct PATCH) + the list of existing requests, each
// opening the approval panel. The masked bank refreshes automatically once a VBC
// request reaches APPLIED (final approve writes the Vendor bank columns).
export function VendorBankSection({ companyId, supplierId, vendorId, vendor }: Props) {
  const [requestOpen, setRequestOpen] = useState(false);
  const [openReq, setOpenReq] = useState<VendorBankChange | null>(null);
  const { data: requests = [] } = useVendorBankChanges(companyId, supplierId, vendorId);

  const hasBank = !!vendor.bankAccountNumber;

  return (
    <div data-testid={`vendor-bank-${vendorId}`}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div className="meta">บัญชีธนาคารปลายทาง · สาขา {vendor.branchCode}</div>
        <Can permission="vendorbank.request">
          <button type="button" className="btn sm" onClick={() => setRequestOpen(true)}>
            ขอเปลี่ยนบัญชีธนาคาร
          </button>
        </Can>
      </div>

      <div
        className="tbl-wrap"
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 13,
        }}
      >
        {hasBank ? (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>
                เลขบัญชี
              </div>
              <div className="code">{maskAccount(vendor.bankAccountNumber)}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>
                ธนาคาร
              </div>
              <div>{vendor.bankCode ?? '—'}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>
                ชื่อบัญชี
              </div>
              <div>{vendor.accountHolderName ?? '—'}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>
                ประเภท
              </div>
              <div>{vendor.bankAccountType ?? '—'}</div>
            </div>
          </div>
        ) : (
          <div className="muted">— ยังไม่ได้บันทึกบัญชีธนาคาร</div>
        )}
      </div>

      {requests.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="meta" style={{ marginBottom: 4 }}>
            คำขอเปลี่ยนบัญชีธนาคาร
          </div>
          <div className="tbl-wrap">
            <table className="tbl" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th>เลขที่</th>
                  <th>วันที่</th>
                  <th>บัญชีใหม่</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpenReq(r)}>
                    <td className="code">
                      <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{r.voucherNo}</a>
                    </td>
                    <td className="muted">{r.requestDate ? fmtDate(r.requestDate) : '—'}</td>
                    <td className="code">{maskAccount(r.newBankAccountNumber)}</td>
                    <td>{STATUS_LABEL[r.status] ?? r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {requestOpen && (
        <VendorBankChangeModal
          companyId={companyId}
          supplierId={supplierId}
          vendorId={vendorId}
          onClose={() => setRequestOpen(false)}
        />
      )}
      {openReq && companyId && (
        <VendorBankChangeApprovalPanel
          companyId={companyId}
          supplierId={supplierId}
          vendorId={vendorId}
          request={openReq}
          onClose={() => setOpenReq(null)}
        />
      )}
    </div>
  );
}
